import React from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Link
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRightOutlined,
  HomeOutlined,
  ReceiptLongOutlined,
  AppsOutlined,
  RequestQuoteOutlined,
  DataThresholdingOutlined,
  WorkOutline,
  GavelOutlined,
  CodeOutlined,
  BadgeOutlined,
  PermMediaOutlined,
  Facebook, YouTube, Instagram, LinkedIn, GradeOutlined
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import logoImage from "assets/logo.png";
import { useSelector } from 'react-redux';

const navItems = [
  { text: "DASHBOARD", icon: <HomeOutlined />, role: ['sales', 'finance', 'admin', 'superadmin'] },
  { text: "SALES", icon: <ReceiptLongOutlined />, role: ['sales', 'finance', 'admin', 'superadmin'] },
  { text: "APPS", icon: <AppsOutlined />, role: ['sales', 'finance', 'admin', 'superadmin'] },
  { text: "FINANCE", icon: <RequestQuoteOutlined />, role: ['admin', 'finance', 'superadmin'] },
  { text: "DATA", icon: <DataThresholdingOutlined/>, role: ['sales', 'finance', 'admin', 'superadmin'] },
  { text: "CONTENT", icon: <PermMediaOutlined />, role: ['sales', 'admin', 'superadmin'] },
  { text: "LOYALTY", icon: <GradeOutlined />, role: ['superadmin'] },
  { text: "HR", icon: <BadgeOutlined />, role: ['finance', 'admin', 'superadmin'] },
  { text: "DEV", icon: <CodeOutlined />, role: ['superadmin'] },
  { text: "LEGAL", icon: <GavelOutlined/>, role: ['finance', 'admin', 'superadmin'] },
  { text: "ABOUT", icon: <WorkOutline/>, role: ['finance', 'admin', 'superadmin'] },
];

const Sidebar = ({
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);
  const user = useSelector((state) => state.global.user);
  const displayUsername = user?.displayName ? user.displayName : "Loading...";
  
  const userProfileImage = useSelector((state) => {
  const userPic = state.global.user?.photoURL;
    return userPic ? `${userPic}` : "../assets/profile.jpeg";
  });
  const userRole = useSelector((state) => state.global.user?.role);
  const filteredNavItems = navItems.filter(item => 
    !item.role || item.role.includes(userRole)
  );
  console.log('USER ROLE: ', userRole);

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.grey[100],
              backgroundColor: theme.palette.background.alt,
              boxSixing: "border-box",
              borderWidth: isNonMobile ? 0 : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box
          display="flex"
          flexDirection="column"
          height="100%"
          >
            <Box display="flex"
              justifyContent="center"
              backgroundColor="#000000"
            >
              <FlexBetween color={theme.palette.secondary.main}>
                <Box display="flex" justifyContent="center" alignItems="center" gap="0.5rem">
                  <img
                    src={logoImage} 
                    alt="logo"
                    width={141}
                  />
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
              
            </Box>
            <Box>
              
            <Box display="flex" alignItems="center" m="1rem 2rem 1.5rem 2rem">
              {/* Profile Picture */}
              <Box
                component="img"
                alt="profile"
                src={userProfileImage}
                height="40px"
                width="40px"
                borderRadius="50%"
                sx={{ objectFit: "cover" }}
              />
              {/* User Information */}
              <Box flexShrink={1} ml={2} minWidth={0}>
                {/* Username */}
                <Typography
                  noWrap 
                  fontWeight="bold"
                  fontSize="1rem"
                  sx={{
                    color: theme.palette.secondary.main,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayUsername}
                </Typography>
                {/* User Role */}
                <Typography
                  noWrap
                  fontSize="0.6rem"
                  sx={{
                    color: theme.palette.grey[10], // Make sure this color is visible against the background
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userRole}
                </Typography>
              </Box>
            </Box>
            <Divider />
            </Box>
            
            <List>
              {filteredNavItems.map(({ text, icon, externalUrl }) => {
                if (!icon) {
                  return (
                    <Typography key={text} sx={{ m: "2rem 0 1rem 3rem" }}>
                      {text}
                    </Typography>
                  );
                }
                const lcText = text.toLowerCase();

                const handleNavigation = (itemText, url) => {
                  if (url) {
                    // If an external URL is provided, open it in a new tab
                    window.open(url, '_blank');
                  } else {
                    // If no external URL, proceed with internal navigation
                    navigate(`/${itemText}`);
                    setActive(itemText);
                  }
                };
                
                return (
                  <ListItem key={text} disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigation(lcText, externalUrl)}
                      sx={{
                        backgroundColor:
                          active === lcText
                            ? theme.palette.secondary[300]
                            : "transparent",
                        color:
                          active === lcText
                            ? theme.palette.primary[600]
                            : theme.palette.grey[100],
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "2rem",
                          color:
                            active === lcText
                              ? theme.palette.primary[600]
                              : theme.palette.secondary[200],
                        }}
                      >
                        {icon}
                      </ListItemIcon>
                      <ListItemText primary={text} />
                      {active === lcText && (
                        <ChevronRightOutlined sx={{ ml: "auto" }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            <Box>
              <Box sx={{ p: 2, mt: 18, mb: 0 }}>
                <Divider sx={{ mb: 2 }}/>
                <Box
                  display="flex"
                  justifyContent="center"
                  mb={1}
                >
                  <IconButton onClick={() => window.open('https://www.instagram.com/flairflowofficial', '_blank')}><Instagram /></IconButton>
                  <IconButton onClick={() => window.open('https://www.youtube.com/channel/UCdb_h7czvDp2_Etac0KH7Ww', '_blank')}><YouTube /></IconButton>
                  <IconButton onClick={() => window.open('https://www.facebook.com/FLAIRFLOWOFFICIAL', '_blank')}><Facebook /></IconButton>
                  <IconButton onClick={() => window.open('https://www.linkedin.com/company/flair-flow/', '_blank')}><LinkedIn /></IconButton>
                </Box>
                <Typography
                  align="center"
                  fontSize="0.7rem"
                  sx={{ color: theme.palette.grey[600] }}
                > 
                  <Link
                    href="https://kodawise.xyz"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: 'inherit', 
                      textDecoration: 'none',
                      '&:hover': {
                        color: 'orange'
                      }
                    }}
                  >
                  Powered by KodaWise
                  </Link>
                </Typography>
            </Box>
          </Box>
        </Box>

        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;